import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { buildLabDataQueryParams } from '../utils/labData';
import { logError, logInfo } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'lab_data_client';

/**
 * @param {Object|Array|string|null} errPayload
 * @returns {string[]}
 */
export const formatLabDataErrors = errPayload => {
  if (errPayload == null) {
    return ['Request failed. Please try again.'];
  }
  if (typeof errPayload === 'string') {
    return [errPayload];
  }
  if (Array.isArray(errPayload)) {
    return errPayload.map(item => String(item));
  }
  if (errPayload.detail) {
    return [String(errPayload.detail)];
  }
  const messages = [];
  Object.entries(errPayload).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(v => messages.push(`${key}: ${v}`));
    } else {
      messages.push(String(value));
    }
  });
  return messages.length ? messages : ['Request failed. Please try again.'];
};

/**
 * @param {Object} options
 * @returns {string}
 */
function buildLabDataUrl(path, options = {}) {
  const params = buildLabDataQueryParams(options);
  const query = params.toString();
  return query ? `${BACKEND_URL}${path}?${query}` : `${BACKEND_URL}${path}`;
}

/**
 * GET /v1/lab-data/spend/summary/
 */
export const getSpendSummary = async (token, options = {}) => {
  const url = buildLabDataUrl('lab-data/spend/summary/', options);
  try {
    logInfo(LOG_CONTEXT, 'getSpendSummary start', {
      from: options.from,
      to: options.to,
    });
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
    });
    logInfo(LOG_CONTEXT, 'getSpendSummary success');
    return { success: true, data: response.data };
  } catch (error) {
    logError(LOG_CONTEXT, 'getSpendSummary failed', error.response?.data ?? error);
    return {
      success: false,
      messages: formatLabDataErrors(error.response?.data),
      status: error.response?.status,
    };
  }
};

/**
 * GET /v1/lab-data/spend/by-period/
 */
export const getSpendByPeriod = async (token, options = {}) => {
  const url = buildLabDataUrl('lab-data/spend/by-period/', {
    granularity: 'month',
    ...options,
  });
  try {
    logInfo(LOG_CONTEXT, 'getSpendByPeriod start', {
      from: options.from,
      to: options.to,
    });
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
    });
    return { success: true, data: response.data };
  } catch (error) {
    logError(LOG_CONTEXT, 'getSpendByPeriod failed', error.response?.data ?? error);
    return {
      success: false,
      messages: formatLabDataErrors(error.response?.data),
      status: error.response?.status,
    };
  }
};

/**
 * GET /v1/lab-data/spend/by-supplier/
 */
export const getSpendBySupplier = async (token, options = {}) => {
  const url = buildLabDataUrl('lab-data/spend/by-supplier/', options);
  try {
    logInfo(LOG_CONTEXT, 'getSpendBySupplier start');
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
    });
    return { success: true, data: response.data };
  } catch (error) {
    logError(LOG_CONTEXT, 'getSpendBySupplier failed', error.response?.data ?? error);
    return {
      success: false,
      messages: formatLabDataErrors(error.response?.data),
      status: error.response?.status,
    };
  }
};

/**
 * GET /v1/lab-data/budgets/
 */
export const getBudgetLinesBurn = async (token, options = {}) => {
  const url = buildLabDataUrl('lab-data/budgets/', options);
  try {
    logInfo(LOG_CONTEXT, 'getBudgetLinesBurn start');
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
    });
    return { success: true, data: response.data };
  } catch (error) {
    logError(LOG_CONTEXT, 'getBudgetLinesBurn failed', error.response?.data ?? error);
    return {
      success: false,
      messages: formatLabDataErrors(error.response?.data),
      status: error.response?.status,
    };
  }
};

/**
 * GET /v1/lab-data/budgets/{id}/
 */
export const getBudgetDetail = async (token, budgetLineId, options = {}) => {
  const url = buildLabDataUrl(`lab-data/budgets/${budgetLineId}/`, options);
  try {
    logInfo(LOG_CONTEXT, 'getBudgetDetail start', { budgetLineId });
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
    });
    return { success: true, data: response.data };
  } catch (error) {
    logError(LOG_CONTEXT, 'getBudgetDetail failed', error.response?.data ?? error);
    return {
      success: false,
      messages: formatLabDataErrors(error.response?.data),
      status: error.response?.status,
    };
  }
};
